<template>
    <div>
        <div role="tablist" class="tabs tabs-boxed bg-base-100 mx-2 mt-2 shadow-sm rounded-xl !flex">
            <a role="tab" class="tab tab-active">{{ $tt('刮削报告') }}</a>
        </div>
        <div>
            <div class="flex flex-col md:flex-row">
                <div
                    class="bg-base-100 md:w-1/2 md:h-60 shadow-xl rounded-xl m-2 flex flex-col md:flex-row md:justify-between">
                    <div class="p-8">
                        <div class="mt-4 indent-4 text-gray-500">
                            <div class="flex mb-2">
                                <div class="font-medium text-md">{{ $tt('标题:') }}</div>
                                <div>{{ $tt(detailData.name) }}</div>
                            </div>
                            <div class="flex mb-2">
                                <div class="font-medium text-md">{{ $tt('创建时间:') }}</div>
                                <div>{{ detailData.created_at }}</div>
                            </div>
                            <div class="flex mb-2">
                                <div class="font-medium text-md">{{ $tt('执行状态:') }}</div>
                                <div>{{ $tt(detailData.status_display) }}</div>
                                <div v-if="detailData.status === 'running'">
                                    <button class="btn btn-xs btn-outline btn-error ml-2" @click="handleStop">{{ $tt('停止') }}</button>
                                </div>
                                <div v-if="detailData.status === 'stop' && canResumeTask(detailData.name)">
                                    <button class="btn btn-xs btn-outline btn-success ml-2" @click="handleResume">{{ $tt('恢复') }}</button>
                                </div>
                            </div>
                            <div class="flex mb-2">
                                <div class="font-medium text-md">{{ $tt('运行时间:') }}</div>
                                <div>{{ detailData.run_time }}</div>
                            </div>
                            <div class="flex mb-2">
                                <div class="font-medium text-md">{{ $tt('结束时间:') }}</div>
                                <div>{{ detailData.complete_at }}</div>
                            </div>
                        </div>
                    </div>
                    <img src="/static/dist/img/play_music.svg" alt="Album" class="h-64 z-1"/>
                </div>
                <div class="m-2 flex flex-col grow">
                    <div class="stats stats-vertical md:stats-horizontal shadow-xl">
                        <div class="stat">
                            <div class="stat-figure text-primary">
                                <svg-icon 
                                    name="check"
                                    class="fill-secondary"
                                    width="32"
                                    height="32"
                                />
                            </div>
                            <div class="stat-title">{{ $tt('已完成') }}</div>
                            <div class="stat-value text-primary cursor-pointer">{{ detailData.static.complete_count }}</div>
                            <div class="stat-desc">{{ $tt('包括成功失败和未匹配') }}</div>
                        </div>

                        <div class="stat">
                            <div class="stat-figure text-secondary">
                                <svg-icon 
                                    name="document"
                                    class="fill-primary"
                                    width="32"
                                    height="32"
                                />
                            </div>
                            <div class="stat-title">{{ $tt('总数') }}</div>
                            <div class="stat-value text-secondary">{{ detailData.static.total_count }}</div>
                            <div class="stat-desc">{{ $tt('总共个数') }}</div>
                        </div>

                        <div class="stat">
                            <div class="stat-figure text-secondary">
                                <div class="radial-progress text-primary"
                                     :style="`--value: ${detailData.static.complete_rate};`" role="progressbar">
                                    {{ detailData.static.complete_rate }}%
                                </div>
                            </div>
                            <div class="stat-value">{{ detailData.static.complete_rate }}</div>
                            <div class="stat-title">{{ $tt('完成进度') }}</div>
                            <div class="stat-desc text-secondary">{{ $tt('完成进度百分比') }}</div>
                        </div>
                    </div>
                    <div class="stats stats-vertical md:stats-horizontal shadow-xl mt-4">

                        <div class="stat">
                            <div class="stat-figure text-secondary">
                                <svg-icon 
                                    name="check"
                                    class="fill-primary"
                                    width="32"
                                    height="32"
                                />
                            </div>
                            <div class="stat-title">{{ $tt('成功') }}</div>
                            <div class="stat-value text-secondary cursor-pointer">
                                {{ detailData.static.success_count }}
                            </div>
                            <div class="stat-desc">{{ $tt('成功完成并匹配上的任务') }}</div>
                        </div>

                        <div class="stat">
                            <div class="stat-figure text-secondary">
                                <svg-icon 
                                    name="close"
                                    class="fill-secondary"
                                    width="32"
                                    height="32"
                                />
                            </div>
                            <div class="stat-title">{{ $tt('失败') }}</div>
                            <div class="stat-value text-primary cursor-pointer">{{ detailData.static.fail_count }}</div>
                            <div class="stat-desc">{{ $tt('文件异常或网络异常的任务') }}</div>
                        </div>

                        <div class="stat">
                            <div class="stat-figure text-secondary">
                                <svg-icon 
                                    name="minus"
                                    width="32"
                                    height="32"
                                />
                            </div>
                            <div class="stat-title">{{ $tt('待候选') }}</div>
                            <div class="stat-value">{{ detailData.static.candidate_count }}</div>
                            <div class="stat-desc">{{ $tt('未命中当前匹配模式规则') }}</div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
        <div v-if="isTaskType(detailData.name, 'duplicateCheck')">
            <duplicate-log-detail :detail-data="detailData"></duplicate-log-detail>
        </div>
        <div v-else-if="isTaskType(detailData.name, 'importLibrary')">
            <import-music-detail :detail-data="detailData"></import-music-detail>
        </div>
        <div v-else-if="isTaskType(detailData.name, 'mergeArtists')">
            <artist-merge-log-detail :detail-data="detailData"></artist-merge-log-detail>
        </div>
        <div v-else>
            <auto-edit-log-detail
                :detail-data="detailData"
                @stop-detail="handleStopDetail"
                @beixuan="handleBeixuan"
                @ai-candidate="handleAiCandidate"
                @batch-ai-candidate="handleBatchAiCandidate"
                @edit-detail="handleEditDetail"
                @finish="handleFinish"
                @delete="handleDelete"
                @delete-all-fail="handleDeleteAllFail"
            ></auto-edit-log-detail>
        </div>
        <div class="drawer">
            <input id="id3-detail" type="checkbox" class="drawer-toggle" v-model="isShowResourceId32"/>
            <div class="drawer-content">
            </div>
            <div class="drawer-side">
                <label for="id3-detail" aria-label="close sidebar" class="drawer-overlay"></label>
                <div class="p-4 min-h-full bg-base-100 text-base-content" style="width: 400px;">
                    <div>
                        <music-meta-data :music-info="musicInfo" :reload-img="reloadImg" :drawer-mode="'drawer'">
                        </music-meta-data>
                    </div>
                </div>
            </div>
        </div>
        <div class="drawer drawer-end">
            <input id="resource-id3-detail" type="checkbox" class="drawer-toggle" v-model="isShowResourceId3"/>
            <div class="drawer-content">
            </div>
            <div class="drawer-side">
                <label for="resource-id3-detail" aria-label="close sidebar" class="drawer-overlay"></label>
                <div class="p-4 min-h-full bg-base-100 text-base-content" style="width: 800px;">
                    <resource :song-list="selectSongList" :section3-loading="section3Loading"></resource>
                </div>
            </div>
        </div>

        <!-- AI 候选项推荐确认弹窗 -->
        <dialog id="ai_candidate_modal" class="modal">
            <div class="modal-box max-w-2xl">
                <h3 class="font-bold text-lg text-primary mb-4">
                    <i class="fas fa-robot mr-2"></i>{{ $tt('AI 推荐候选项') }}
                </h3>

                <!-- Loading 状态 -->
                <div v-if="aiRecommendation.loading" class="py-12">
                    <div class="flex flex-col items-center justify-center">
                        <span class="loading loading-spinner loading-lg text-primary mb-4"></span>
                        <p class="text-base-content/70">{{ $tt('AI 正在分析候选项...') }}</p>
                        <p class="text-sm text-base-content/50 mt-2">{{ $tt('请稍候，这可能需要几秒钟') }}</p>
                    </div>
                </div>

                <!-- 错误状态 -->
                <div v-else-if="aiRecommendation.error" class="py-4">
                    <div class="alert alert-error">
                        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <div class="font-bold">{{ $tt('AI 分析失败') }}</div>
                            <div class="text-sm">{{ aiRecommendation.error }}</div>
                        </div>
                    </div>
                    <div class="modal-action">
                        <form method="dialog">
                            <button class="btn btn-sm">{{ $tt('关闭') }}</button>
                        </form>
                    </div>
                </div>

                <!-- 成功状态 - 显示推荐结果 -->
                <div v-else class="py-4">
                    <div class="alert alert-info mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="stroke-current shrink-0 w-6 h-6">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        <span>{{ $tt('AI 已分析音乐元数据和所有候选项，为您推荐最匹配的结果') }}</span>
                    </div>

                    <!-- 推荐的候选项信息 -->
                    <div class="bg-primary/10 p-4 rounded-lg border border-primary/30 mb-4">
                        <div class="flex items-center justify-between mb-3">
                            <h4 class="font-semibold text-primary text-base">{{ $tt('推荐候选项') }}</h4>
                            <div class="badge badge-primary">
                                {{ $tt('置信度: {percent}%', { percent: aiRecommendation.confidencePercent }) }}
                            </div>
                        </div>

                        <div class="space-y-2">
                            <div class="flex items-start">
                                <span class="font-medium text-sm w-20 text-base-content/70">{{ $tt('标题：') }}</span>
                                <span class="text-sm flex-1">{{ aiRecommendation.candidate.name }}</span>
                            </div>
                            <div class="flex items-start">
                                <span class="font-medium text-sm w-20 text-base-content/70">{{ $tt('艺术家：') }}</span>
                                <span class="text-sm flex-1">{{ aiRecommendation.candidate.artist }}</span>
                            </div>
                            <div class="flex items-start">
                                <span class="font-medium text-sm w-20 text-base-content/70">{{ $tt('专辑：') }}</span>
                                <span class="text-sm flex-1">{{ aiRecommendation.candidate.album }}</span>
                            </div>
                            <div class="flex items-start">
                                <span class="font-medium text-sm w-20 text-base-content/70">{{ $tt('来源：') }}</span>
                                <span class="text-sm flex-1">{{ aiRecommendation.candidate.resource }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- 推荐理由 -->
                    <div class="bg-base-200 p-4 rounded-lg">
                        <h4 class="font-semibold text-sm mb-2 flex items-center">
                            <i class="fas fa-lightbulb mr-2 text-warning"></i>
                            {{ $tt('推荐理由') }}
                        </h4>
                        <p class="text-sm text-base-content/80 whitespace-pre-line">{{ aiRecommendation.reason }}</p>
                    </div>

                    <div class="modal-action">
                        <form method="dialog">
                            <button class="btn btn-sm">{{ $tt('取消') }}</button>
                            <button class="btn btn-primary btn-sm" @click="confirmAiCandidate">
                                {{ $tt('应用此候选项') }}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </dialog>

        <!-- 删除单个文件确认弹窗 -->
        <dialog id="delete_file_modal" class="modal">
            <div class="modal-box">
                <h3 class="font-bold text-lg text-error mb-4 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {{ $tt('警告：删除文件操作') }}
                </h3>

                <div class="alert alert-warning mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>{{ $tt('此操作将会永久删除音乐文件，删除后无法恢复！') }}</span>
                </div>

                <div class="bg-base-200 p-4 rounded-lg mb-4">
                    <p class="text-sm text-base-content/70 mb-2">{{ $tt('即将删除的文件：') }}</p>
                    <p class="font-medium break-all">{{ deleteConfirm.fileName }}</p>
                </div>

                <p class="text-error font-semibold mb-4">{{ $tt('请谨慎操作，确定要删除吗？') }}</p>

                <div class="modal-action">
                    <form method="dialog">
                        <button class="btn btn-sm" @click="cancelDelete">{{ $tt('取消') }}</button>
                        <button class="btn btn-error btn-sm ml-2" @click="confirmDelete">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            {{ $tt('确认删除') }}
                        </button>
                    </form>
                </div>
            </div>
        </dialog>

        <!-- 批量删除文件确认弹窗 -->
        <dialog id="delete_all_fail_modal" class="modal">
            <div class="modal-box">
                <h3 class="font-bold text-lg text-error mb-4 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {{ $tt('警告：批量删除文件操作') }}
                </h3>

                <div class="alert alert-error mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{{ $tt('此操作将会永久删除所有异常失败的音乐文件，删除后无法恢复！') }}</span>
                </div>

                <div class="stats shadow mb-4 w-full">
                    <div class="stat">
                        <div class="stat-figure text-error">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </div>
                        <div class="stat-title">{{ $tt('即将删除') }}</div>
                        <div class="stat-value text-error">{{ deleteConfirm.fileCount }}</div>
                        <div class="stat-desc">{{ $tt('个异常失败的音乐文件') }}</div>
                    </div>
                </div>

                <p class="text-error font-semibold mb-4">{{ $tt('请谨慎操作，确定要全部删除吗？') }}</p>

                <div class="modal-action">
                    <form method="dialog">
                        <button class="btn btn-sm" @click="cancelDeleteAll">{{ $tt('取消') }}</button>
                        <button class="btn btn-error btn-sm ml-2" @click="confirmDeleteAll">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            {{ $tt('确认全部删除') }}
                        </button>
                    </form>
                </div>
            </div>
        </dialog>
    </div>
</template>

<script>

import duplicateLogDetail from "@/views/log/duplicateLogDetail";
import importMusicDetail from "@/views/log/importMusicDetail";
import artistMergeLogDetail from "@/views/log/artistMergeLogDetail";
import autoEditLogDetail from "@/views/log/autoEditLogDetail";
import Resource from '@/views/home/resource'
import MusicMetaData from '@/views/home/musicMetaData'
import SvgIcon from '@/components/SvgIcon.vue'

const TASK_NAME_ALIASES = {
    autoScrape: ['自动刮削', 'auto scrape', 'auto-scrape', 'auto_scrape'],
    importLibrary: ['导入收藏', 'import library', 'import-collection', 'import_collection'],
    duplicateCheck: ['重复检查', 'duplicate check', 'duplicate-check', 'duplicate_check'],
    mergeArtists: ['合并相似艺术家', 'merge similar artists', 'artist merge', 'artist_merge']
}

export default {
    name: 'log-detail',
    components: {
        duplicateLogDetail,
        importMusicDetail,
        artistMergeLogDetail,
        autoEditLogDetail,
        Resource,
        MusicMetaData,
        SvgIcon
    },
    data() {
        return {
            id: this.$route.params.id,
            detailData: {
                'success': [],
                'fail': [],
                'mode_success': [],
                'miss_artist_album': [],
                'miss_artist': [],
                'miss_album': [],
                'miss_tag': [],
                'running': [],
                'candidate': [],
                'skip': [],
                'static': {
                    'complete_count': 0
                }
            },
            isOpen: true,
            timer: null,
            isShowResourceId3: false,
            isShowResourceId32: false,
            section3Loading: false,
            selectSongList: [],
            reloadImg: true,
            musicInfo: {},
            fullPath: '',
            currentDetailId: '',
            aiRecommendation: {
                item: null,
                candidate: {
                    name: '',
                    artist: '',
                    album: '',
                    resource: ''
                },
                confidence: 0,
                confidencePercent: 0,
                reason: '',
                loading: false,
                error: null
            },
            deleteConfirm: {
                item: null,
                fileName: '',
                fileCount: 0
            }
        }
    },
    computed: {
        isSuccess: {
            get() {
                return this.detailData.success.length > 0
            },
            set() {}
        },
        isFail: {
            get() {
                return this.detailData.fail.length > 0
            },
            set(){}
        },
        isModeSuccess: {
            get() {
                return this.detailData.mode_success.length > 0
            },
            set(){}
        },
        isMissAA() {
            return this.detailData.miss_artist_album.length > 0
        },
        isMissArtist() {
            return this.detailData.miss_artist.length > 0
        },
        isMissAlbum() {
            return this.detailData.miss_album.length > 0
        },
        isMissTag() {
            return this.detailData.miss_tag.length > 0
        },
        isRunning() {
            return this.detailData.running.length > 0
        },
        isSkip() {
            return this.detailData.skip.length > 0
        }
    },
    mounted() {
        this.handlePolling()
    },
    beforeDestroy() {
        this.setTimerNull() // 组件销毁前清空定时器
    },
    methods: {
        normalizeTaskName(name) {
            return String(name || '')
                .trim()
                .toLowerCase()
                .replace(/[_-]/g, ' ')
                .replace(/\s+/g, ' ')
        },
        isTaskType(name, taskType) {
            const normalizedName = this.normalizeTaskName(name)
            const aliases = TASK_NAME_ALIASES[taskType] || []
            return aliases.some((alias) => this.normalizeTaskName(alias) === normalizedName)
        },
        canResumeTask(name) {
            return this.isTaskType(name, 'autoScrape') || this.isTaskType(name, 'importLibrary')
        },
        fetchDetails() {
            this.$api.Task.getAuditLogDetail({id: this.id}).then((res) => {
                if (res.result) {
                    this.detailData = res.data
                }
            })
        },
        // 轮询开启
        handlePolling() {
            // 查询接口
            this.fetchDetails()
            this.timer = setInterval(() => {
                if (['success', 'fail', 'miss', 'stop'].indexOf(this.detailData.status) !== -1) {
                    this.setTimerNull()
                } else {
                    this.fetchDetails()
                }
            }, 3000)
        },
        setTimerNull() {
            clearInterval(this.timer)
            this.timer = null
        },
        handleStop() {
            this.$api.Task.stopAuditLogDetail({id: this.id}).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                            title: this.$tt('停止成功！'),
                            type: 'success',
                            time: 2000,
                        })
                    this.fetchDetails()
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('停止失败！'),
                        content: res.message,
                        type: 'error',
                        time: 2000,
                    })
                }
            })
        },
        handleResume() {
            this.$api.Task.resumeAuditLog({id: this.id}).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                            title: this.$tt('恢复成功！'),
                            type: 'success',
                            time: 2000,
                        })
                    this.fetchDetails()
                    this.handlePolling()
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('恢复失败！'),
                        content: res.message,
                        type: 'error',
                        time: 2000,
                    })
                }
            })
        },
        handleStopDetail(id) {
            this.$api.Task.stopAuditLogDetailRun({id: id}).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                            title: this.$tt('停止成功！'),
                            type: 'success',
                            time: 2000,
                        })
                    this.fetchDetails()
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('停止失败！'),
                        content: res.message,
                        type: 'error',
                        time: 2000,
                    })
                }
            })
        },
        handleBeixuan(item) {
            this.currentDetailId = item.id
            this.fullPath = item.path
            this.isShowResourceId32 = true
            this.isShowResourceId3 = true
            this.selectSongList = item.candidate_list
            const parentPath = item.path.replace(item.name, '')
            this.$api.Task.musicId3({'file_path': parentPath, 'file_name': item.name}).then((res) => {
                if(res.result) {
                    this.musicInfo = res.data
                }
            })
        },
        async handleAiCandidate(item) {
            // 重置状态并显示 loading
            this.aiRecommendation = {
                item: item,
                candidate: {
                    name: '',
                    artist: '',
                    album: '',
                    resource: ''
                },
                confidence: 0,
                confidencePercent: 0,
                reason: '',
                loading: true,
                error: null
            }

            // 立即打开 modal 显示 loading
            document.getElementById('ai_candidate_modal').showModal()

            try {
                // 获取音乐文件元数据
                const parentPath = item.path.replace(item.name, '')
                const id3Res = await this.$api.Task.musicId3({
                    'file_path': parentPath,
                    'file_name': item.name
                })

                if (!id3Res.result) {
                    this.aiRecommendation.loading = false
                    this.aiRecommendation.error = id3Res.msg || this.$tt('获取音乐元数据失败')
                    return
                }

                // 调用 AI 决策 API
                const aiRes = await this.$api.agents.askSelectCandidate({
                    'track_info': id3Res.data,
                    'candidate_list': item.candidate_list
                })

                if (aiRes.result) {
                    const aiData = aiRes.data

                    if (aiData.selected_index >= 0) {
                        const selectedCandidate = item.candidate_list[aiData.selected_index]

                        // 更新 AI 推荐信息
                        this.aiRecommendation.candidate = selectedCandidate
                        this.aiRecommendation.confidence = aiData.confidence
                        this.aiRecommendation.confidencePercent = (aiData.confidence * 100).toFixed(0)
                        this.aiRecommendation.reason = aiData.reason
                        this.aiRecommendation.loading = false
                    } else {
                        this.aiRecommendation.loading = false
                        this.aiRecommendation.error = this.$tt('AI 分析后认为所有候选项匹配度较低，建议手动选择')
                    }
                } else {
                    this.aiRecommendation.loading = false
                    this.aiRecommendation.error = aiRes.message || this.$tt('AI 决策失败')
                }
            } catch (error) {
                this.aiRecommendation.loading = false
                this.aiRecommendation.error = error.message || this.$tt('AI 决策失败')
            }
        },
        async confirmAiCandidate() {
            // 用户确认后应用候选项
            await this.applyCandidate(this.aiRecommendation.item, this.aiRecommendation.candidate)
        },
        async handleBatchAiCandidate() {
            // 批量 AI 候选处理
            if (!this.detailData.candidate || this.detailData.candidate.length === 0) {
                this.$myMsg.notify({
                    title: this.$tt('没有待候选项'),
                    type: 'warning',
                    time: 2000
                })
                return
            }

            // 确认对话框
            const confirmed = confirm(this.$tt('确定要对 {count} 个待候选项进行批量 AI 候选吗？', {
                count: this.detailData.candidate_count
            }))
            if (!confirmed) {
                return
            }

            try {
                // 调用批量 AI 候选接口
                const res = await this.$api.agents.batchAiCandidate({
                    'audit_log_id': this.detailData.id
                })

                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('批量 AI 候选任务已启动'),
                        content: this.$tt('任务正在后台处理中，请稍后刷新查看结果'),
                        type: 'success',
                        time: 3000
                    })
                    // 刷新详情
                    setTimeout(() => {
                        this.fetchDetails()
                    }, 2000)
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('启动批量 AI 候选失败'),
                        content: res.msg || this.$tt('未知错误'),
                        type: 'error',
                        time: 3000
                    })
                }
            } catch (error) {
                this.$myMsg.notify({
                    title: this.$tt('批量 AI 候选失败'),
                    content: error.message || this.$tt('未知错误'),
                    type: 'error',
                    time: 3000
                })
            }
        },
        async applyCandidate(item, candidate) {
            // 调用现有的候选确认 API
            const res = await this.$api.Task.changeStatusAuditLogDetailRun({
                'id': item.id,
                'candidate': candidate
            })

            if (res.result) {
                this.$myMsg.notify({
                    title: this.$tt('候选项已应用'),
                    type: 'success',
                    time: 2000
                })
                this.fetchDetails()  // 刷新详情列表
            } else {
                this.$myMsg.notify({
                    title: this.$tt('应用候选项失败'),
                    content: res.msg,
                    type: 'error',
                    time: 3000
                })
            }
        },
        handleEditDetail(item) {
            this.fullPath = item.path
            this.isShowResourceId32 = true
            const parentPath = item.path.replace(item.name, '')
            this.$api.Task.musicId3({'file_path': parentPath, 'file_name': item.name}).then((res) => {
                if(res.result) {
                    this.musicInfo = res.data
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('读取元数据失败'),
                        content: res.message,
                        type: 'error',
                        time: 2000,
                    }) 
                }
            })
        },
        handleCopy(k, v) {
            if (k === 'lyric') {
                this.resource = localStorage.getItem('resource') ? localStorage.getItem('resource') : 'netease'
                const resurce = this.resource !== 'smart_tag' ? this.resource : v.resource
                this.$api.Task.fetchLyric({'song_id': v.id, 'resource': resurce}).then((res) => {
                    if (res.result) {
                        if (res.data !== '') {
                            this.musicInfo['lyrics'] = res.data
                            this.handleCopy('composer', this.musicInfo['lyrics'])
                        } else {
                            this.$myMsg.notify({
                                title: this.$tt('未找到歌词！'),
                                type: 'error',
                                time: 2000,
                            })
                        }
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('未找到歌词！'),
                            type: 'error',
                            time: 2000,
                        })
                    }
                })
            } else if (k === 'composer') {
                this.$api.Task.extractLyric({'lyc': v}).then((res) => {
                    if (res.result) {
                        if (res.data !== '') {
                            this.musicInfo['composer'] = res.data.composer
                            this.musicInfo['lyricist'] = res.data.lyricist
                        } else {
                            console.log()
                        }
                    } else {
                        console.log()
                    }
                })
            }
            else if (k === 'album_img') {
                this.musicInfo[k] = ''
                this.musicInfo['artwork'] = v
                this.musicInfo['files1'] = [
                    {
                        name: 'new.png',
                        status: 'done',
                        url: v
                    }
                ]
                this.reloadImg = false
                this.$nextTick(() => {
                    this.reloadImg = true
                })
            } else if (k === 'lyric_tran') {
                this.musicInfo['lyrics'] = v
            } else {
                this.musicInfo[k] = v
            }
        },
        copyAll(item) {
            this.handleCopy('title', item.name)
            this.handleCopy('year', item.year)
            this.handleCopy('lyric', item)
            this.handleCopy('album', item.album)
            this.handleCopy('artist', item.artist)
            this.handleCopy('album_img', item.album_img)
            this.handleCopy('tracknumber', item.tracknumber)
            this.handleCopy('discnumber', item.discnumber)
            this.handleCopy('totaltracks', item.totaltracks)
            this.handleCopy('totaldiscs', item.totaldiscs)
        },
        handleClick() {
            this.$api.Task.changeStatusAuditLogDetailRun({id: this.currentDetailId}).then((res) =>{
                if (res.result) {
                    this.fetchDetails()
                }
            })
        },
        toggleLock() {
            this.isShowResourceId3 = true
            this.section3Loading = true
            this.$api.Task.fetchId3Title({
                title: this.musicInfo.title,
                artist: this.musicInfo.artist,
                album: this.musicInfo.album,
                full_path: this.fullPath,
                resource: localStorage.getItem('resource') ? localStorage.getItem('resource') : 'netease'
            }).then((res) => {
                this.selectSongList = res.data
                this.section3Loading = false
            })
        },
        handleDelete(item) {
            // 设置删除确认信息并显示弹窗
            this.deleteConfirm.item = item
            this.deleteConfirm.fileName = item.name
            document.getElementById('delete_file_modal').showModal()
        },
        cancelDelete() {
            // 取消删除，清空状态
            this.deleteConfirm.item = null
            this.deleteConfirm.fileName = ''
        },
        confirmDelete() {
            // 确认删除
            const item = this.deleteConfirm.item
            if (!item) return

            this.$api.Task.deleteFiles({'log_id': this.id, 'mode': 'single', 'paths': [], 'detail_id': item.id}).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('删除成功！'),
                        type: 'success',
                        time: 2000,
                    })
                    this.fetchDetails()
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('删除失败！'),
                        content: res.message || this.$tt('未知错误'),
                        type: 'error',
                        time: 2000,
                    })
                }
            })

            // 清空状态
            this.cancelDelete()
        },
        handleFinish(item) {
            this.$api.Task.finishFiles({'log_id': this.id, 'mode': 'single', 'paths': [], 'detail_id': item.id}).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('完成成功！'),
                        type: 'success',
                        time: 2000,
                    })
                    this.fetchDetails()
                }
            })
        },
        handleDeleteAllFail() {
            // 设置批量删除确认信息并显示弹窗
            const failCount = this.detailData.fail_count || this.detailData.fail?.length || 0
            this.deleteConfirm.fileCount = failCount
            document.getElementById('delete_all_fail_modal').showModal()
        },
        cancelDeleteAll() {
            // 取消批量删除，清空状态
            this.deleteConfirm.fileCount = 0
        },
        confirmDeleteAll() {
            // 确认批量删除
            this.$api.Task.deleteFiles({'log_id': this.id, 'mode': 'fail', 'paths': []}).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('删除成功！'),
                        type: 'success',
                        time: 2000,
                    })
                    this.fetchDetails()
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('删除失败！'),
                        content: res.message || this.$tt('未知错误'),
                        type: 'error',
                        time: 2000,
                    })
                }
            })

            // 清空状态
            this.cancelDeleteAll()
        }
    }
}
</script>

<style scoped>

</style>
